import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Row,
  Col,
  FormFeedback,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap";
import { toast } from "react-toastify";
import { siteService } from "../../../services/siteService";
import { roomService } from "../../../services/roomService";
import { roomTypeService } from "../../../services/roomTypeService";
import { deviceService } from "../../../services/deviceService";
import { deviceTypeService } from "../../../services/deviceTypeService";
import { Home, Plus, Check, X } from "react-feather";

import RoomTree from "./RoomTree";
import EditRoomModal from "./EditRoomModal";
import CreateDeviceModal from "./CreateDeviceModal";
import DeviceDetailsModal from "./DeviceDetailsModal";
import AssignUserToRoomModal from "./AssignUserToRoomModal";
import AssignUserToSiteModal from "./AssignUserToSiteModal";
import SitePermissionManager from "./SitePermissionManager";
import ConfirmDeleteDialog from "../../Common/ConfirmDeleteDialog";

/**
 * SiteModal - Main parent component. Handles site-level state and renders
 * the site form + tabs. Child modals are imported and rendered here.
 */
const SiteModal = ({ isOpen, toggle, mode = "view", siteData = null, onSuccess }) => {
  const [formData, setFormData] = useState({ name: "", partner: "" });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("details");
  const [siteDetails, setSiteDetails] = useState(null);
  const [loadingSiteDetails, setLoadingSiteDetails] = useState(false);
  const [expandedRooms, setExpandedRooms] = useState(new Set());

  // Add room state
  const [addingRoomTo, setAddingRoomTo] = useState(false);
  const [newRoomName, setNewRoomName] = useState("");
  const [newRoomTypeId, setNewRoomTypeId] = useState("");
  const [creatingRoom, setCreatingRoom] = useState(false);

  // Room types
  const [roomTypes, setRoomTypes] = useState([]);
  const [loadingRoomTypes, setLoadingRoomTypes] = useState(false);

  // Edit room
  const [editingRoom, setEditingRoom] = useState(null);
  const [editRoomData, setEditRoomData] = useState({ name: "", roomTypeId: "" });
  const [updatingRoom, setUpdatingRoom] = useState(false);

  // Delete room
  const [deletingRoom, setDeletingRoom] = useState(null);
  const [deletingRoomLoading, setDeletingRoomLoading] = useState(false);

  // Create device
  const [creatingDeviceForRoom, setCreatingDeviceForRoom] = useState(null);
  const [deviceFormData, setDeviceFormData] = useState({ name: "", description: "", deviceTypeId: "" });
  const [creatingDevice, setCreatingDevice] = useState(false);
  const [deviceTypes, setDeviceTypes] = useState([]);
  const [loadingDeviceTypes, setLoadingDeviceTypes] = useState(false);

  // View device
  const [viewingDevice, setViewingDevice] = useState(null);

  // Assign user to room
  const [assigningUserToRoom, setAssigningUserToRoom] = useState(null);


  // ==================== Initialization ====================

  useEffect(() => {
    if (isOpen) {
      setActiveTab("details");
      if (mode === "create") {
        const partnerUuid = localStorage.getItem("partner_uuid") || "";
        setFormData({ name: "", partner: partnerUuid });
        setSiteDetails(null);
      } else if (mode === "edit" && siteData) {
        setFormData({
          name: siteData.name || "",
          partner: siteData.partnerUuid || "",
        });
        fetchSiteDetails(siteData.id);
      }
      setErrors({});
    }
  }, [isOpen, mode, siteData]);

  // ==================== Site API ====================

  const fetchSiteDetails = async (siteId) => {
    setLoadingSiteDetails(true);
    try {
      const response = await siteService.getById(siteId);
      setSiteDetails(response);
    } catch (error) {
      console.error("Error fetching site details:", error);
      toast.error("Failed to load site details");
    } finally {
      setLoadingSiteDetails(false);
    }
  };


  const getAllRooms = () => {
    if (!siteDetails?.rooms) return [];
    return siteDetails.rooms;
  };

  // ==================== Room Types ====================

  const fetchRoomTypes = async () => {
    setLoadingRoomTypes(true);
    try {
      const response = await roomTypeService.search({ name: "", page: 0, size: 1000 });
      setRoomTypes(response.content || []);
    } catch (error) {
      console.error("Error fetching room types:", error);
      toast.error("Failed to load room types");
    } finally {
      setLoadingRoomTypes(false);
    }
  };

  // ==================== Device Types ====================

  const fetchDeviceTypes = async () => {
    setLoadingDeviceTypes(true);
    try {
      const response = await deviceTypeService.search({ name: "", page: 0, size: 1000 });
      setDeviceTypes(response.content || []);
    } catch (error) {
      console.error("Error fetching device types:", error);
      toast.error("Failed to load device types");
    } finally {
      setLoadingDeviceTypes(false);
    }
  };

  // ==================== Room Tree Expand/Collapse ====================

  const toggleRoom = (roomId) => {
    const newExpanded = new Set(expandedRooms);
    if (newExpanded.has(roomId)) {
      newExpanded.delete(roomId);
    } else {
      newExpanded.add(roomId);
    }
    setExpandedRooms(newExpanded);
  };

  // ==================== Add Room ====================

  const handleAddRoom = () => {
    setAddingRoomTo(true);
    setNewRoomName("");
    setNewRoomTypeId("");
    if (roomTypes.length === 0) fetchRoomTypes();
  };

  const handleCancelAddRoom = () => {
    setAddingRoomTo(false);
    setNewRoomName("");
    setNewRoomTypeId("");
  };

  const handleCreateRoom = async () => {
    if (!newRoomName.trim()) {
      toast.error("Room name is required");
      return;
    }

    setCreatingRoom(true);
    try {
      const roomData = {
        name: newRoomName.trim(),
        siteId: siteData.id,
        ...(newRoomTypeId && { roomTypeId: parseInt(newRoomTypeId) }),
      };

      await roomService.create(roomData);
      toast.success("Room created successfully");
      await fetchSiteDetails(siteData.id);

      setAddingRoomTo(false);
      setNewRoomName("");
      setNewRoomTypeId("");
    } catch (error) {
      console.error("Error creating room:", error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Failed to create room");
      }
    } finally {
      setCreatingRoom(false);
    }
  };

  // ==================== Edit Room ====================

  const handleEditRoom = (room) => {
    setEditingRoom(room);
    setEditRoomData({
      name: room.name,
      roomTypeId: room.roomTypeId ? room.roomTypeId.toString() : "",
    });
    if (roomTypes.length === 0) fetchRoomTypes();
  };

  const handleCancelEditRoom = () => {
    setEditingRoom(null);
    setEditRoomData({ name: "", roomTypeId: "" });
  };

  const handleUpdateRoom = async () => {
    if (!editRoomData.name.trim()) {
      toast.error("Room name is required");
      return;
    }

    setUpdatingRoom(true);
    try {
      const updateData = {
        name: editRoomData.name.trim(),
        siteId: siteData.id,
        ...(editRoomData.roomTypeId && { roomTypeId: parseInt(editRoomData.roomTypeId) }),
      };

      await roomService.update(editingRoom.id, updateData);
      toast.success("Room updated successfully");
      await fetchSiteDetails(siteData.id);

      setEditingRoom(null);
      setEditRoomData({ name: "", roomTypeId: "" });
    } catch (error) {
      console.error("Error updating room:", error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Failed to update room");
      }
    } finally {
      setUpdatingRoom(false);
    }
  };

  // ==================== Delete Room ====================

  const handleDeleteRoom = (room) => {
    setDeletingRoom(room);
  };

  const handleCancelDeleteRoom = () => {
    setDeletingRoom(null);
  };

  const handleConfirmDeleteRoom = async () => {
    if (!deletingRoom) return;

    setDeletingRoomLoading(true);
    try {
      await roomService.remove(deletingRoom.id);
      toast.success("Room deleted successfully");
      await fetchSiteDetails(siteData.id);
      setDeletingRoom(null);
    } catch (error) {
      console.error("Error deleting room:", error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Failed to delete room");
      }
    } finally {
      setDeletingRoomLoading(false);
    }
  };

  // ==================== Create Device ====================

  const handleCreateDevice = (room) => {
    setCreatingDeviceForRoom(room);
    setDeviceFormData({ name: "", description: "", deviceTypeId: "" });
    fetchDeviceTypes();
  };

  const handleCancelCreateDevice = () => {
    setCreatingDeviceForRoom(null);
    setDeviceFormData({ name: "", description: "", deviceTypeId: "" });
  };

  const handleConfirmCreateDevice = async () => {
    if (!deviceFormData.name.trim()) {
      toast.error("Device name is required");
      return;
    }
    if (!deviceFormData.deviceTypeId) {
      toast.error("Device type is required");
      return;
    }

    setCreatingDevice(true);
    try {
      const deviceData = {
        name: deviceFormData.name.trim(),
        description: deviceFormData.description.trim(),
        roomId: creatingDeviceForRoom.id,
        deviceTypeId: parseInt(deviceFormData.deviceTypeId),
      };

      await deviceService.create(deviceData);
      toast.success("Device created successfully");
      await fetchSiteDetails(siteData.id);

      setCreatingDeviceForRoom(null);
      setDeviceFormData({ name: "", description: "", deviceTypeId: "" });
    } catch (error) {
      console.error("Error creating device:", error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Failed to create device");
      }
    } finally {
      setCreatingDevice(false);
    }
  };

  // ==================== View Device ====================

  const handleViewDevice = (device) => {
    setViewingDevice(device);
  };

  const handleCloseDeviceView = () => {
    setViewingDevice(null);
  };

  // ==================== Assign User to Room ====================

  const handleAssignUser = (room) => {
    setAssigningUserToRoom(room);
  };

  const handleCloseAssignUser = () => {
    setAssigningUserToRoom(null);
  };

  // ==================== Form Handling ====================

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Site name is required";
    } else if (formData.name.length < 2) {
      newErrors.name = "Site name must be at least 2 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (mode === "view") {
      toggle();
      return;
    }

    if (!validateForm()) {
      toast.error("Please fix the form errors");
      return;
    }

    setLoading(true);
    try {
      const siteBody = { name: formData.name };
      let response;
      if (mode === "create") {
        response = await siteService.create(siteBody);
        toast.success("Site created successfully");
      } else if (mode === "edit") {
        response = await siteService.update(siteData.id, siteBody);
        toast.success("Site updated successfully");
      }
      if (onSuccess) onSuccess(response);
      toggle();
    } catch (error) {
      console.error(`Error ${mode === "create" ? "creating" : "updating"} site:`, error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error(`Failed to ${mode === "create" ? "create" : "update"} site`);
      }
    } finally {
      setLoading(false);
    }
  };

  const getModalTitle = () => {
    switch (mode) {
      case "create":
        return "Create New Site";
      case "edit":
        return "Edit Site";
      case "view":
        return "Site Details";
      default:
        return "Site";
    }
  };

  // ==================== Render ====================

  return (
    <>
      <Modal isOpen={isOpen} toggle={toggle} size="lg">
        <ModalHeader toggle={toggle}>{getModalTitle()}</ModalHeader>
        <Form onSubmit={handleSubmit}>
          <ModalBody>
            {mode === "edit" && (
              <>
                <Nav tabs>
                  <NavItem>
                    <NavLink
                      className={activeTab === "details" ? "active" : ""}
                      onClick={() => setActiveTab("details")}
                      style={{ cursor: "pointer" }}
                    >
                      Site Details
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={activeTab === "rooms" ? "active" : ""}
                      onClick={() => setActiveTab("rooms")}
                      style={{ cursor: "pointer" }}
                    >
                      Rooms ({siteDetails?.rooms?.length || 0})
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={activeTab === "users" ? "active" : ""}
                      onClick={() => setActiveTab("users")}
                      style={{ cursor: "pointer" }}
                    >
                      Users
                    </NavLink>
                  </NavItem>
                </Nav>
                <TabContent activeTab={activeTab} className="mt-3">
                  <TabPane tabId="details">
                    <FormGroup>
                      <Label for="name">
                        Site Name <span className="text-danger">*</span>
                      </Label>
                      <Input
                        id="name"
                        type="text"
                        placeholder="Enter site name"
                        value={formData.name}
                        onChange={(e) => handleChange("name", e.target.value)}
                        invalid={!!errors.name}
                      />
                      <FormFeedback>{errors.name}</FormFeedback>
                    </FormGroup>

                    <FormGroup>
                      <Label for="partner">Partner UUID</Label>
                      <Input
                        id="partner"
                        type="text"
                        value={formData.partner}
                        disabled
                      />
                    </FormGroup>
                  </TabPane>

                  <TabPane tabId="rooms">
                    {loadingSiteDetails ? (
                      <div className="text-center p-4">
                        <div className="spinner-border" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <h6 className="mb-0">Rooms</h6>
                          <Button
                            color="primary"
                            size="sm"
                            onClick={handleAddRoom}
                            disabled={addingRoomTo !== false}
                          >
                            <Plus size={14} className="me-1" />
                            Add Room
                          </Button>
                        </div>

                        {/* Inline add room form */}
                        {addingRoomTo === true && (
                          <div
                            style={{
                              padding: "12px",
                              marginBottom: "12px",
                              display: "flex",
                              alignItems: "center",
                              backgroundColor: "#fef3c7",
                              borderLeft: "4px solid #f59e0b",
                              borderRadius: "0 6px 6px 0",
                              boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                            }}
                          >
                            <div
                              style={{
                                width: "28px",
                                height: "28px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                borderRadius: "6px",
                                backgroundColor: "#fbbf24",
                                color: "white",
                                marginRight: "12px",
                              }}
                            >
                              <Home size={14} />
                            </div>
                            <Input
                              type="text"
                              placeholder="Enter root room name..."
                              value={newRoomName}
                              onChange={(e) => setNewRoomName(e.target.value)}
                              onKeyPress={(e) => {
                                if (e.key === "Enter") {
                                  handleCreateRoom();
                                }
                              }}
                              disabled={creatingRoom}
                              style={{
                                flex: 1,
                                marginRight: "8px",
                                border: "2px solid #fbbf24",
                                borderRadius: "6px",
                                fontWeight: "500",
                              }}
                              autoFocus
                            />
                            <Input
                              type="select"
                              value={newRoomTypeId}
                              onChange={(e) => setNewRoomTypeId(e.target.value)}
                              disabled={creatingRoom || loadingRoomTypes}
                              style={{
                                width: "160px",
                                marginRight: "8px",
                                border: "2px solid #fbbf24",
                                borderRadius: "6px",
                              }}
                            >
                              <option value="">
                                {loadingRoomTypes ? "Loading..." : "Room Type (opt.)"}
                              </option>
                              {roomTypes.map((rt) => (
                                <option key={rt.id} value={rt.id}>
                                  {rt.name}
                                </option>
                              ))}
                            </Input>
                            <Button
                              color="success"
                              size="sm"
                              onClick={() => handleCreateRoom(null)}
                              disabled={creatingRoom}
                              className="me-1"
                              style={{ borderRadius: "6px", padding: "6px 12px" }}
                            >
                              <Check size={14} />
                            </Button>
                            <Button
                              color="secondary"
                              size="sm"
                              onClick={handleCancelAddRoom}
                              disabled={creatingRoom}
                              style={{ borderRadius: "6px", padding: "6px 12px" }}
                            >
                              <X size={14} />
                            </Button>
                          </div>
                        )}

                        {siteDetails?.rooms && siteDetails.rooms.length > 0 ? (
                          <div
                            className="border rounded"
                            style={{
                              maxHeight: "400px",
                              overflowY: "auto",
                              backgroundColor: "#ffffff",
                              padding: "8px",
                            }}
                          >
                            <RoomTree
                              rooms={siteDetails.rooms}
                              expandedRooms={expandedRooms}
                              onToggleRoom={toggleRoom}
                              onEditRoom={handleEditRoom}
                              onDeleteRoom={handleDeleteRoom}
                              onCreateDevice={handleCreateDevice}
                              onViewDevice={handleViewDevice}
                              onAssignUser={handleAssignUser}
                            />
                          </div>
                        ) : (
                          <div className="text-center text-muted p-4">
                            No rooms available for this site. Click "Add Room" to create one.
                          </div>
                        )}
                      </div>
                    )}
                  </TabPane>

                  {/* Users Tab */}
                  <TabPane tabId="users">
                    <SitePermissionManager
                      site={siteData}
                      siteDetails={siteDetails}
                      onRefresh={() => fetchSiteDetails(siteData.id)}
                    />
                  </TabPane>
                </TabContent>
              </>
            )}

            {mode === "create" && (
              <>
                <FormGroup>
                  <Label for="name">
                    Site Name <span className="text-danger">*</span>
                  </Label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="Enter site name"
                    value={formData.name}
                    onChange={(e) => handleChange("name", e.target.value)}
                    invalid={!!errors.name}
                  />
                  <FormFeedback>{errors.name}</FormFeedback>
                </FormGroup>

                <FormGroup>
                  <Label for="partner">Partner UUID</Label>
                  <Input
                    id="partner"
                    type="text"
                    value={formData.partner}
                    disabled
                  />
                </FormGroup>
              </>
            )}

            {mode === "view" && siteData && (
              <>
                <h6 className="mb-3">Site Details</h6>
                <Row>
                  <Col md="6">
                    <FormGroup>
                      <Label>ID</Label>
                      <Input type="text" value={siteData.id || "-"} disabled />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label>Name</Label>
                      <Input type="text" value={siteData.name || "-"} disabled />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md="12">
                    <FormGroup>
                      <Label>Partner UUID</Label>
                      <Input type="text" value={siteData.partnerUuid || "-"} disabled />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md="6">
                    <FormGroup>
                      <Label>Assigned By</Label>
                      <Input type="text" value={siteData.assignedBy || "-"} disabled />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label>Assigned At</Label>
                      <Input
                        type="text"
                        value={
                          siteData.assignedAt
                            ? new Date(siteData.assignedAt).toLocaleString()
                            : "-"
                        }
                        disabled
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md="6">
                    <FormGroup>
                      <Label>Created At</Label>
                      <Input
                        type="text"
                        value={new Date(siteData.createdAt).toLocaleString()}
                        disabled
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label>Updated At</Label>
                      <Input
                        type="text"
                        value={new Date(siteData.updatedAt).toLocaleString()}
                        disabled
                      />
                    </FormGroup>
                  </Col>
                </Row>
                {siteData.rooms !== null && siteData.rooms !== undefined && (
                  <Row>
                    <Col md="12">
                      <FormGroup>
                        <Label>Rooms</Label>
                        <Input
                          type="textarea"
                          value={
                            typeof siteData.rooms === "object"
                              ? JSON.stringify(siteData.rooms, null, 2)
                              : siteData.rooms || "-"
                          }
                          disabled
                          rows="4"
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                )}
              </>
            )}
          </ModalBody>

          <ModalFooter>
            {mode === "view" ? (
              <Button color="secondary" onClick={toggle}>
                Close
              </Button>
            ) : mode === "edit" && (activeTab === "rooms" || activeTab === "users") ? (
              <Button color="secondary" onClick={toggle}>
                Close
              </Button>
            ) : (
              <>
                <Button color="secondary" onClick={toggle} disabled={loading}>
                  Cancel
                </Button>
                <Button color="primary" type="submit" disabled={loading}>
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      {mode === "create" ? "Creating..." : "Updating..."}
                    </>
                  ) : mode === "create" ? (
                    "Create Site"
                  ) : (
                    "Update Site"
                  )}
                </Button>
              </>
            )}
          </ModalFooter>
        </Form>
      </Modal>

      {/* Edit Room Modal */}
      <EditRoomModal
        isOpen={!!editingRoom}
        roomData={editRoomData}
        onRoomDataChange={setEditRoomData}
        onSave={handleUpdateRoom}
        onCancel={handleCancelEditRoom}
        loading={updatingRoom}
        roomTypes={roomTypes}
        loadingRoomTypes={loadingRoomTypes}
      />

      {/* Delete Room Confirmation */}
      <ConfirmDeleteDialog
        isOpen={!!deletingRoom}
        title="Delete Room"
        itemName={deletingRoom?.name}
        warningText={undefined}
        actionText="Delete Room"
        loadingText="Deleting..."
        onClose={handleCancelDeleteRoom}
        onConfirm={handleConfirmDeleteRoom}
        loading={deletingRoomLoading}
      />

      {/* Create Device Modal */}
      <CreateDeviceModal
        isOpen={!!creatingDeviceForRoom}
        room={creatingDeviceForRoom}
        formData={deviceFormData}
        onFormDataChange={setDeviceFormData}
        onConfirm={handleConfirmCreateDevice}
        onCancel={handleCancelCreateDevice}
        loading={creatingDevice}
        deviceTypes={deviceTypes}
        loadingDeviceTypes={loadingDeviceTypes}
      />

      {/* Device Details Modal */}
      <DeviceDetailsModal
        isOpen={!!viewingDevice}
        device={viewingDevice}
        onClose={handleCloseDeviceView}
        allRooms={getAllRooms()}
        deviceTypes={deviceTypes}
        loadingDeviceTypes={loadingDeviceTypes}
        onFetchDeviceTypes={fetchDeviceTypes}
        onSuccess={() => {
          if (siteData) fetchSiteDetails(siteData.id);
        }}
      />

      {/* Assign User to Room Modal */}
      <AssignUserToRoomModal
        isOpen={!!assigningUserToRoom}
        room={assigningUserToRoom}
        onClose={handleCloseAssignUser}
        siteId={siteData?.id}
        onSuccess={() => {
          if (siteData) fetchSiteDetails(siteData.id);
        }}
      />
    </>
  );
};

export default SiteModal;
